import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Line, Marker } from "react-simple-maps";
import { GLOBAL_CONTENT, MAP_ORIGINS, MAP_DESTINATIONS, MAP_ROUTES } from "../data/global";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export const GlobalReach = ({ lang }) => (
  <section id="global" data-testid="global-section" className="relative overflow-hidden border-t border-white/10 bg-[#0C1018] px-5 py-20 sm:px-8 sm:py-24">
    <div className="mx-auto max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-12 max-w-2xl"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-400">{GLOBAL_CONTENT.eyebrow[lang]}</span>
        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">{GLOBAL_CONTENT.title[lang]}</h2>
        <p className="mt-3 text-base text-slate-400">{GLOBAL_CONTENT.sub[lang]}</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8 }}
        className="gold-trace relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0D14] p-2 sm:p-4"
        data-testid="global-map-card"
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 128, center: [-50, 25] }}
          width={900}
          height={460}
          style={{ width: "100%", height: "auto" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#161C28"
                  stroke="#232B3A"
                  strokeWidth={0.5}
                  style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                />
              ))
            }
          </Geographies>

          {MAP_ROUTES.map((r, i) => (
            <Line
              key={r.id}
              from={r.from}
              to={r.to}
              stroke="#E5A93B"
              strokeWidth={1.6}
              strokeLinecap="round"
              className="route-arc"
              style={{ animationDelay: `${i * 0.35}s` }}
            />
          ))}

          {MAP_ORIGINS.map((o) => (
            <Marker key={o.id} coordinates={o.coords} data-testid={`map-origin-${o.id}`}>
              <circle r={7} fill="rgba(229,169,59,0.35)" className="map-pulse" />
              <circle r={3.2} fill="#E5A93B" />
              <text textAnchor={o.anchor || "middle"} y={o.dy ?? -12} className="map-label">
                {typeof o.label === "string" ? o.label : o.label[lang]}
              </text>
            </Marker>
          ))}

          {MAP_DESTINATIONS.map((d) => (
            <Marker key={d.id} coordinates={d.coords} data-testid={`map-destination-${d.id}`}>
              <circle r={8} fill="rgba(248,250,252,0.25)" className="map-pulse" />
              <circle r={3.6} fill="#F8FAFC" stroke="#E5A93B" strokeWidth={1.5} />
              <text textAnchor={d.anchor || "middle"} y={d.dy ?? 16} className="map-label map-label-dest">
                {d.label}
              </text>
            </Marker>
          ))}
        </ComposableMap>
      </motion.div>

      <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">{GLOBAL_CONTENT.origins[lang]}</span>
          {MAP_ORIGINS.map((o) => (
            <span key={o.id} data-testid={`legend-origin-${o.id}`} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200">
              <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(229,169,59,0.8)]" />
              {typeof o.label === "string" ? o.label : o.label[lang]}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">{GLOBAL_CONTENT.destinations[lang]}</span>
          {MAP_DESTINATIONS.map((d) => (
            <span key={d.id} data-testid={`legend-destination-${d.id}`} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200">
              <span className="h-2 w-2 rounded-full bg-white" />
              {d.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);
